import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { LoginSettings } from '../loginSetting.model';
import { LoginService } from '../login.service';
import { User } from '../../../shared-components/user/user.model';
import { UserService } from '../../../shared-components/user/user.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  mustBeDifferent, notSameAsUsername,
  passwordsMatchForChangePassword, preventPasswordReuse, requiresMixedLettersNumbers,
  requiresNumeric,
  requiresSpecial,
  requiresUpperCase
} from '../change-password/password-validator';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from '../../../shared-components/shared.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css']
})
export class ResetPasswordComponent implements OnInit {

  isDebug: false; // Used for debugging
  LoginSettings: LoginSettings;
  User: User;
  newPasswordError = 'Field is Required';
  repeatNewPasswordError = 'Field is Required';
  resetPasswordForm: FormGroup;
  forgotPasswordParams: any[];
  isLinkExpired = false;
  resetPasswordMessage = '';
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ResetPasswordComponent>,
    private loginService: LoginService,
    private userService: UserService,
    private sharedService: SharedService,
    private router: Router,
    private translateService: TranslateService) {

  }

  ngOnInit() {
    this.User = this.data.user;
    this.isLinkExpired = this.data.isLinkExpired;
    this.resetPasswordMessage = this.data.resetPasswordMessage;
    this.forgotPasswordParams = this.data.forgotPasswordParams;
    this.LoginSettings = this.loginService.LoginSettings;
    this.resetPasswordForm = new FormGroup({
      'newPassword': new FormControl(null, [
        Validators.required,
        Validators.minLength((this.User.UserType == 1 || this.LoginSettings.V10) ? this.LoginSettings.MinimumPasswordLength : 0),
        requiresUpperCase(this.LoginSettings),
        requiresNumeric(this.LoginSettings),
        requiresSpecial(this.LoginSettings),
        mustBeDifferent(this.User, this.LoginSettings), // new password must not equal the current password
        preventPasswordReuse(this.LoginSettings, this.User), // Prevents re-use of expired passwords
        notSameAsUsername(this.User, this.LoginSettings), // new password must not be the same as the userName
        requiresMixedLettersNumbers(this.LoginSettings, this.User) // v9 Only
      ]),
      'repeatNewPassword': new FormControl(null, [
        Validators.required
      ])
    }, { validators: passwordsMatchForChangePassword(this.LoginSettings, this.User) });
    if (this.isDebug) {
      console.log('User', this.User);
      console.log('LoginSettings', this.LoginSettings);
    }
    this.onChanges();
  }

  get newPassword() {
    return this.resetPasswordForm.get('newPassword');
  }

  get repeatNewPassword() {
    return this.resetPasswordForm.get('repeatNewPassword');
  }

  closeClick() {
    // POST: the change password dialog is closed
    this.dialogRef.close();
  }

  saveClick() {
    this.newPassword.markAsTouched();
    this.repeatNewPassword.markAsTouched();
    if (this.resetPasswordForm.invalid == false) {
      this.loginService.saveForgetPassword(this.newPassword.value, 'Forgot Password', this.forgotPasswordParams).subscribe(response => {
        if (response.ok) {
          // Display a toast ie: snackbar
          this.sharedService.showGoodSnackbar();
        }
        this.dialogRef.close();
      });
    }
  }

  onChanges(): void {
    this.resetPasswordForm.valueChanges.subscribe(val => {
      // check new password
      if (this.newPassword.invalid) {
        if (this.newPassword.errors.hasOwnProperty('required')) {
          this.newPasswordError = 'Required Field';
        }
        if (this.newPassword.errors.hasOwnProperty('minlength') && (this.User.UserType == 1 || this.LoginSettings.V10)) {
          this.newPasswordError = `New password must be at least ${this.LoginSettings.MinimumPasswordLength} characters`;
        }
        if (this.newPassword.errors.hasOwnProperty('mustContainUpperCase') && (this.User.UserType == 1 || this.LoginSettings.V10)) {
          this.newPasswordError = 'Must have at least one Uppercase character';
        }
        if (this.newPassword.errors.hasOwnProperty('mustContainNumeric') && (this.User.UserType == 1 || this.LoginSettings.V10)) {
          this.newPasswordError = 'Must have at least one Numeric character';
        }
        if (this.newPassword.errors.hasOwnProperty('mustContainSpecial') && (this.User.UserType == 1 || this.LoginSettings.V10)) {
          this.newPasswordError = 'Must have at least one Special (! @ # $ ...) character';
        }
        if (this.newPassword.errors.hasOwnProperty('mustBeDifferent')) {
          this.newPasswordError = 'New password cannot be the same as the current password';
        }
        if (this.newPassword.errors.hasOwnProperty('notSameAsUsername') && (this.User.UserType == 1 || this.LoginSettings.V10)) {
          this.newPasswordError = 'New password cannot be the same as Login Name';
        }
        if (this.newPassword.errors.hasOwnProperty('requiresMixedLettersNumbers') && (this.User.UserType == 1 || this.LoginSettings.V10)) {
          this.newPasswordError = 'Must have at least 1 alphabetic character (A-Z or a-z) and 1 number (0-9)';
        }
        if (this.newPassword.errors.hasOwnProperty('preventPasswordReuse') && (this.User.UserType == 1 || this.LoginSettings.V10)) {
          this.newPasswordError = 'Password has already expired';
        }
      }
      // Check re-enter password
      if (this.repeatNewPassword.invalid) {
        if (this.repeatNewPassword.errors.hasOwnProperty('required')) {
          this.repeatNewPasswordError = 'Field is Required';
        }
      }
    });
  }
}
